import { useState, useCallback, useEffect, useRef } from 'react'
import { QrCode, Wifi, WifiOff, Loader2, ArrowRight, Camera, Keyboard, X, Clock, Trash2 } from 'lucide-react'
import { parseWsUrl, connect, storeConnection, setupElectronApi, getConnectionHistory, addConnectionToHistory, removeConnectionFromHistory } from '../transport/connection'
import type { ConnectionHistoryEntry } from '../transport/connection'
import type { TransportConnectionState } from '../transport/client'

type PairingMode = 'scan' | 'manual'
type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'error'

interface PairingScreenProps {
  onConnected: (workspaceId?: string) => void
}

export function PairingScreen({ onConnected }: PairingScreenProps) {
  const [mode, setMode] = useState<PairingMode>('manual')
  const [host, setHost] = useState('')
  const [status, setStatus] = useState<ConnectionStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [scannerLoading, setScannerLoading] = useState(false)
  const [history, setHistory] = useState<ConnectionHistoryEntry[]>(() => getConnectionHistory())
  const scannerRef = useRef<HTMLDivElement>(null)
  const html5QrCodeRef = useRef<any>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cleanupRef = useRef<(() => void) | null>(null)

  const handleConnect = useCallback(async (connectHost: string, connectPort: number, connectProtocol: 'ws' | 'wss' = 'ws') => {
    setStatus('connecting')
    setError(null)

    try {
      const client = connect(connectHost, connectPort, undefined, connectProtocol)

      // Set a 20-second connection timeout
      timeoutRef.current = setTimeout(() => {
        cleanupRef.current?.()
        cleanupRef.current = null
        setStatus('error')
        setError('Connection timed out')
      }, 20_000)

      // Listen for connection state changes
      const cleanup = client.onConnectionStateChanged((state: TransportConnectionState) => {
        if (state.status === 'connected') {
          if (timeoutRef.current) clearTimeout(timeoutRef.current)
          timeoutRef.current = null
          cleanupRef.current = null
          cleanup()
          setStatus('connected')
          setupElectronApi(client)

          // Store connection for next launch and add to history
          storeConnection({ host: connectHost, port: connectPort, protocol: connectProtocol })
          addConnectionToHistory({ host: connectHost, port: connectPort, protocol: connectProtocol })
          setHistory(getConnectionHistory())

          // Register workspace immediately so workspace-scoped push events
          // (session:event) are delivered from the very first turn.
          void (async () => {
            try {
              const workspaces = await (window as any).electronAPI.getWorkspaces()
              const workspaceId = workspaces?.[0]?.id as string | undefined

              if (workspaceId) {
                client.setWorkspaceId(workspaceId)
                await (window as any).electronAPI.switchWorkspace(workspaceId).catch(() => {})
                storeConnection({ host: connectHost, port: connectPort, workspaceId, protocol: connectProtocol })
              }

              onConnected(workspaceId)
            } catch {
              onConnected()
            }
          })()
        } else if (state.status === 'failed') {
          if (timeoutRef.current) clearTimeout(timeoutRef.current)
          timeoutRef.current = null
          cleanupRef.current = null
          cleanup()
          setStatus('error')
          setError(state.lastError?.message || 'Connection failed')
        }
      })
      cleanupRef.current = cleanup
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Connection failed')
    }
  }, [onConnected])

  const handleManualConnect = useCallback(() => {
    const h = host.trim()
    if (!h) {
      setError('Please enter a connection URL')
      return
    }

    // Parse the connection URL (e.g., wss://host:port or ws://192.168.1.1:9100)
    const parsed = parseWsUrl(h.includes('://') ? h : `ws://${h}`)
    if (!parsed) {
      setError('Invalid connection URL. Use format: wss://hostname:9100')
      return
    }
    handleConnect(parsed.host, parsed.port, parsed.protocol)
  }, [host, handleConnect])

  const handleCancel = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = null
    cleanupRef.current?.()
    cleanupRef.current = null
    setStatus('idle')
    setError(null)
  }, [])

  const handleQrResult = useCallback((decodedText: string) => {
    // The QR code may encode a full web URL with the WS address in a `connect` query param
    // e.g. https://relay.remote.useorigin.com?connect=ws%3A%2F%2F192.168.1.5%3A9100
    let wsUrl = decodedText
    try {
      const url = new URL(decodedText)
      const connectParam = url.searchParams.get('connect')
      if (connectParam) wsUrl = connectParam
    } catch {
      // Not a valid URL — treat as raw ws:// string
    }

    const parsed = parseWsUrl(wsUrl)
    if (parsed) {
      // Stop scanner
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {})
      }
      setHost(`${parsed.protocol}://${parsed.host}:${parsed.port}`)
      handleConnect(parsed.host, parsed.port, parsed.protocol)
    }
  }, [handleConnect])

  // Start QR scanner when mode is 'scan'
  useEffect(() => {
    if (mode !== 'scan' || !scannerRef.current) return

    setScannerLoading(true)
    let html5QrCode: any = null
    let scannerStarted = false

    import('html5-qrcode').then(({ Html5Qrcode, Html5QrcodeSupportedFormats }) => {
      html5QrCode = new Html5Qrcode('qr-reader', {
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
        verbose: false,
        useBarCodeDetectorIfSupported: true,
      })
      html5QrCodeRef.current = html5QrCode

      return html5QrCode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText: string) => handleQrResult(decodedText),
        () => {}
      ).then(() => {
        scannerStarted = true
        setScannerLoading(false)
      })
    }).catch((err) => {
      setScannerLoading(false)
      const message = err instanceof Error ? err.message : 'Failed to load scanner'
      const isPermissionDenied = message.toLowerCase().includes('permission') || message.toLowerCase().includes('notallowederror')
      setError(isPermissionDenied
        ? 'Camera access was denied. Please allow camera permissions in your device settings and try again.'
        : `Camera error: ${message}`)
      setMode('manual')
    })

    return () => {
      if (html5QrCode && scannerStarted) {
        try { html5QrCode.stop().catch(() => {}) } catch { /* ignore */ }
      }
    }
  }, [mode, handleQrResult])

  const isConnecting = status === 'connecting'

  return (
    <div className="flex h-full items-center justify-center bg-[var(--background)]">
      <div className="w-full max-w-md space-y-8 px-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2 text-[var(--foreground)]">
            <QrCode className="h-8 w-8" />
          </div>
          <h1 className="text-2xl font-semibold text-[var(--foreground)]">
            Origin Relay
          </h1>
          <p className="text-sm text-[var(--muted-foreground)]">
            Connect to your desktop app
          </p>
        </div>

        {/* Mode switcher */}
        <div className="flex rounded-lg border border-[var(--border)] p-1 gap-1">
          <button
            onClick={() => setMode('scan')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              mode === 'scan'
                ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
                : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
            }`}
          >
            <Camera className="h-4 w-4" />
            Scan QR
          </button>
          <button
            onClick={() => setMode('manual')}
            className={`flex-1 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              mode === 'manual'
                ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
                : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'
            }`}
          >
            <Keyboard className="h-4 w-4" />
            Manual
          </button>
        </div>

        {/* QR Scanner */}
        {mode === 'scan' && (
          <div className="space-y-4">
            <div className="relative mx-auto w-full max-w-[300px] aspect-square">
              <div
                id="qr-reader"
                ref={scannerRef}
                className="overflow-hidden rounded-lg border border-[var(--border)] w-full h-full"
              />
              {scannerLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center rounded-lg bg-[var(--background)] border border-[var(--border)]">
                  <Loader2 className="h-8 w-8 animate-spin text-[var(--muted-foreground)]" />
                  <p className="mt-2 text-sm text-[var(--muted-foreground)]">Starting camera...</p>
                </div>
              )}
            </div>
            <p className="text-center text-xs text-[var(--muted-foreground)]">
              Point your camera at the QR code shown in the desktop app's Remote Access settings
            </p>
          </div>
        )}

        {/* Manual entry */}
        {mode === 'manual' && (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); handleManualConnect() }}>
            <div className="space-y-2">
              <label className="text-sm font-medium text-[var(--foreground)]">Connection URL</label>
              <input
                type="text"
                value={host}
                onChange={(e) => setHost(e.target.value)}
                placeholder="e.g., wss://macbook.tailnet:9100"
                disabled={isConnecting}
                className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2.5 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] disabled:opacity-50"
              />
            </div>
            <button
              type="submit"
              disabled={isConnecting}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-[var(--primary)] px-4 py-2.5 text-sm font-medium text-[var(--primary-foreground)] transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  Connect
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
            {isConnecting && (
              <button
                type="button"
                onClick={handleCancel}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--border)] px-4 py-2.5 text-sm font-medium text-[var(--muted-foreground)] transition-opacity hover:opacity-90"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            )}
          </form>
        )}

        {/* Status / Error */}
        {status === 'connected' && (
          <div className="flex items-center justify-center gap-2 rounded-lg bg-emerald-500/10 px-4 py-3 text-sm text-emerald-500">
            <Wifi className="h-4 w-4" />
            Connected
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">
            <WifiOff className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Previous servers */}
        {history.length > 0 && !isConnecting && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-medium text-[var(--muted-foreground)] uppercase tracking-wider">
              <Clock className="h-3.5 w-3.5" />
              Previous Connections
            </div>
            <div className="space-y-1.5">
              {history.map((entry) => (
                <div
                  key={`${entry.protocol}://${entry.host}:${entry.port}`}
                  className="group flex items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-2.5 transition-colors hover:bg-[var(--foreground)]/5"
                >
                  <button
                    type="button"
                    onClick={() => handleConnect(entry.host, entry.port, entry.protocol)}
                    className="flex-1 text-left min-w-0"
                  >
                    <p className="text-sm font-medium text-[var(--foreground)] truncate">
                      {entry.label}
                    </p>
                    <p className="text-xs text-[var(--muted-foreground)]">
                      {formatRelativeTime(entry.lastConnected)}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      removeConnectionFromHistory(entry)
                      setHistory(getConnectionHistory())
                    }}
                    className="shrink-0 p-1 rounded text-[var(--muted-foreground)] opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all"
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function formatRelativeTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)
  if (seconds < 60) return 'Just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(timestamp).toLocaleDateString()
}
